import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Segmented, DatePicker,
  Spin,
  Typography,
  Space,
  Statistic,
  Tooltip,
  Skeleton,
  message,
} from 'antd';
import {
  ReloadOutlined,
  ThunderboltOutlined,
  DashboardOutlined,
  SafetyOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Doughnut, Pie } from 'react-chartjs-2';
import dayjs, { Dayjs } from 'dayjs';
import {
  getDashboardIntegrity,
  getDashboardOverview,
  getBatterySoc,
  IntegrityData,
  OverviewData,
  BatterySocData,
} from '../services/meterService';

// ============================================================
// Register Chart.js components
// ============================================================
ChartJS.register(ArcElement, ChartTooltip, Legend);

const { Text, Title } = Typography;

// ============================================================
// Color palette
// ============================================================
const COLORS = {
  green: '#52c41a',
  yellow: '#fadb14',
  orange: '#fa8c16',
  red: '#f5222d',
  blue: '#1890ff',
  cyan: '#13c2c2',
  purple: '#722ed1',
  gold: '#faad14',
  grey: '#e8e8e8',
  darkGrey: '#bfbfbf',
  textSecondary: '#8c8c8c',
  textPrimary: '#262626',
  white: '#ffffff',
};

// Gauge color per petal type
const PETAL_COLORS: Record<string, string> = {
  pv: '#faad14',          // gold - solar
  gridSupply: '#1890ff',  // blue - grid
  batteryDischarge: '#52c41a', // green - battery out
  gridExport: '#fa8c16',  // orange - grid export
  batteryCharge: '#722ed1', // purple - battery in
  load: '#f5222d',        // red - load
};

// ============================================================
// Types
// ============================================================
type ModeType = 'energy' | 'power';

interface PetalData {
  key: string;
  label: string;
  value: number;
  unit: string;
  color: string;
  // Clock position for layout
  clockPos: number; // 12, 10, 2, 8, 4, 6
  // Chart rotation (degrees) so the gauge opens toward center
  rotation: number;
  description: string;
}

// ============================================================
// Helper: format number
// ============================================================
const formatShort = (val: number): string => {
  if (val >= 10000) return `${(val / 10000).toFixed(1)}万`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}千`;
  return val.toFixed(1);
};

// ============================================================
// Petal layout positions (clock layout)
// Clock face: 12 at top, 6 at bottom
// Each angle measured from center of flower
// ============================================================
const PETAL_LAYOUT = [
  { key: 'pv', clockPos: 12, label: '光伏发电', description: '29+56+167+31号表总和' },
  { key: 'gridSupply', clockPos: 10, label: '国家电网供电', description: '1号表ImpEP' },
  { key: 'batteryDischarge', clockPos: 2, label: '储能放电', description: '30号表ExpEP' },
  { key: 'gridExport', clockPos: 8, label: '向电网倒送', description: '1号表ExpEP' },
  { key: 'batteryCharge', clockPos: 4, label: '向储能充电', description: '30号表ImpEP' },
  { key: 'load', clockPos: 6, label: '负载用电', description: '2号+34号变压器总和' },
];

// ============================================================
// Dashboard Component
// ============================================================
interface DashboardProps {
  user?: { id: number; name: string; role: string; };
  onLogout?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  // ---- State ----
  const [mode, setMode] = useState<ModeType>('energy');
  const [loading, setLoading] = useState(true);
  const [integrityLoading, setIntegrityLoading] = useState(true);
  const [socLoading, setSocLoading] = useState(true);

  // Date/time
  const [startTime, setStartTime] = useState<Dayjs>(dayjs().startOf('day'));
  const [endTime, setEndTime] = useState<Dayjs>(dayjs());

  // Data
  const [integrityData, setIntegrityData] = useState<IntegrityData | null>(null);
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [batterySoc, setBatterySoc] = useState<BatterySocData | null>(null);

  // ---- Data Fetching ----
  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);

      const [integrity, overview, soc] = await Promise.all([
        getDashboardIntegrity(15).catch((err) => {
          console.warn('Integrity fetch failed:', err);
          return null;
        }),
        getDashboardOverview(
          mode,
          startTime.format('YYYY-MM-DD HH:mm:ss'),
          endTime.format('YYYY-MM-DD HH:mm:ss'),
        ).catch((err) => {
          console.warn('Overview fetch failed:', err);
          return null;
        }),
        getBatterySoc().catch((err) => {
          console.warn('Battery SOC fetch failed:', err);
          return null;
        }),
      ]);

      if (integrity) setIntegrityData(integrity);
      if (overview) setOverviewData(overview);
      if (soc) setBatterySoc(soc);
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      message.error('获取仪表盘数据失败');
    } finally {
      setLoading(false);
      setIntegrityLoading(false);
      setSocLoading(false);
    }
  }, [mode, startTime, endTime]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // ---- Compute petal data from overview ----
  const petals = useMemo((): PetalData[] => {
    if (!overviewData) return [];

    const unit = mode === 'energy' ? 'kWh' : 'kW';

    return PETAL_LAYOUT.map((layout) => {
      let value = 0;
      switch (layout.key) {
        case 'pv':
          value = overviewData.pv?.total || 0;
          break;
        case 'gridSupply':
          value = overviewData.gridSupply || 0;
          break;
        case 'batteryDischarge':
          value = overviewData.batteryDischarge || 0;
          break;
        case 'gridExport':
          value = overviewData.gridExport || 0;
          break;
        case 'batteryCharge':
          value = overviewData.batteryCharge || 0;
          break;
        case 'load':
          value = overviewData.loadTotal || 0;
          break;
      }

      return {
        key: layout.key,
        label: layout.label,
        value,
        unit,
        color: PETAL_COLORS[layout.key] || COLORS.blue,
        clockPos: layout.clockPos,
        rotation: getPetalRotation(layout.clockPos),
        description: layout.description,
      };
    });
  }, [overviewData, mode]);

  // ---- Integrity Pie Chart Data ----
  const integrityChartData = useMemo(() => {
    if (!integrityData) return null;
    return {
      labels: ['正常(≤1周期)', '1-2倍周期', '2-3倍周期', '>3倍周期'],
      datasets: [
        {
          data: [
            integrityData.green,
            integrityData.yellow,
            integrityData.orange,
            integrityData.red,
          ],
          backgroundColor: [COLORS.green, COLORS.yellow, COLORS.orange, COLORS.red],
          borderColor: [COLORS.green, COLORS.yellow, COLORS.orange, COLORS.red],
          borderWidth: 1,
          hoverOffset: 8,
        },
      ],
    };
  }, [integrityData]);

  // ---- Find max value among petals for scaling ----
  const maxPetalValue = useMemo(() => {
    if (petals.length === 0) return 100;
    const max = Math.max(...petals.map((p) => p.value));
    return max > 0 ? max * 1.25 : 100; // Add 25% headroom, min 100
  }, [petals]);

  // ---- Gauge chart configs ----
  const getGaugeData = useCallback(
    (petal: PetalData) => {
      const val = Math.max(petal.value, 0);
      const remaining = Math.max(maxPetalValue - val, 0);
      return {
        datasets: [
          {
            data: [val, remaining],
            backgroundColor: [petal.color, COLORS.grey],
            borderColor: [petal.color, COLORS.grey],
            borderWidth: 1,
            circumference: 180,
            rotation: petal.rotation,
            cutout: '70%',
            spacing: 0,
          },
        ],
      };
    },
    [maxPetalValue],
  );

  const gaugeOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        tooltip: { enabled: false },
        legend: { display: false },
      },
      animation: {
        animateRotate: true,
        duration: 800,
      },
    }),
    [],
  );

  // ---- Battery SOC Chart Data ----
  const socChartData = useMemo(() => {
    const soc = batterySoc?.currentSoc ?? 50;
    return {
      datasets: [
        {
          data: [soc, 100 - soc],
          backgroundColor: [
            soc >= 50 ? COLORS.green : soc >= 20 ? COLORS.gold : COLORS.red,
            COLORS.grey,
          ],
          borderColor: [COLORS.white, COLORS.white],
          borderWidth: 2,
          cutout: '80%',
          circumference: 360,
          rotation: 0,
        },
      ],
    };
  }, [batterySoc]);

  const socOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        tooltip: { enabled: false },
        legend: { display: false },
      },
      animation: {
        animateRotate: true,
        duration: 1000,
      },
    }),
    [],
  );

  // ---- Handle mode/date changes ----
  const handleModeChange = useCallback((val: ModeType) => {
    setMode(val);
    if (val === 'power') {
      // Power mode: use current time for both start and end
      const now = dayjs();
      setStartTime(now);
      setEndTime(now);
    } else {
      // Energy mode: reset to today
      setStartTime(dayjs().startOf('day'));
      setEndTime(dayjs());
    }
  }, []);

  const handleDateTimeChange = useCallback((start: Dayjs, end: Dayjs) => {
    setStartTime(start);
    setEndTime(end);
  }, []);

  // ---- Petal rotation helper ----
  // Each gauge is a half-circle arc. We rotate the arc so its opening
  // faces the center of the flower layout.
  // Using canvas coordinates: 0°=right(3h), 90°=down(6h), 180°=left(9h), 270°=up(12h)
  // Arc with circumference=180 covers from rotation to rotation+180 clockwise.
  // The "gap" (opening) is in the direction of rotation-90 (or rotation+270).
  // We want the gap to face toward the flower center.
  function getPetalRotation(clockPos: number): number {
    // clockPos 12 → center is below (direction = 90° in canvas)
    // clockPos 6  → center is above (direction = 270°)
    // clockPos 10 → center is lower-right (~30°)
    // clockPos 2  → center is lower-left (~150°)
    // clockPos 8  → center is upper-right (~330°)
    // clockPos 4  → center is upper-left (~210°)

    const centerDirectionMap: Record<number, number> = {
      12: 90,   // center is down from top
      10: 30,   // center is lower-right from upper-left
      2: 150,   // center is lower-left from upper-right
      8: 330,   // center is upper-right from lower-left  (or -30°)
      4: 210,   // center is upper-left from lower-right
      6: 270,   // center is up from bottom
    };

    // Gap direction = rotation - 90 (mod 360)
    // So rotation = gap_direction + 90
    const gapDir = centerDirectionMap[clockPos] ?? 90;
    return (gapDir + 90) % 360;
  }

  // ============================================================
  // Render: Integrity Pie Card
  // ============================================================
  const renderIntegrityCard = () => (
    <Card
      title={
        <Space>
          <SafetyOutlined />
          <span>电表采集完整率</span>
        </Space>
      }
      size="small"
      style={{ height: '100%' }}
      extra={
        <Space size={4}>
          <Text style={{ fontSize: 12, color: COLORS.textSecondary }}>
            周期: 15分钟
          </Text>
          <Tooltip title="刷新">
            <ReloadOutlined
              style={{ cursor: 'pointer', color: COLORS.blue }}
              onClick={fetchAllData}
            />
          </Tooltip>
        </Space>
      }
    >
      {integrityLoading || !integrityChartData ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <div style={{ width: 180, height: 180, flexShrink: 0 }}>
            <Pie
              data={integrityChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
                        const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0';
                        return `${ctx.label}: ${ctx.parsed}台 (${pct}%)`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
          <div style={{ flex: 1, paddingLeft: 16 }}>
            <Statistic
              title="总采集率"
              value={integrityData?.greenPct ?? 0}
              suffix="%"
              valueStyle={{
                color:
                  (integrityData?.greenPct ?? 0) >= 95
                    ? COLORS.green
                    : (integrityData?.greenPct ?? 0) >= 80
                    ? COLORS.gold
                    : COLORS.red,
                fontSize: 28,
                fontWeight: 600,
              }}
            />
            <div style={{ marginTop: 12 }}>
              {[
                { label: '正常', color: COLORS.green, count: integrityData?.green ?? 0 },
                { label: '1-2倍', color: COLORS.yellow, count: integrityData?.yellow ?? 0 },
                { label: '2-3倍', color: COLORS.orange, count: integrityData?.orange ?? 0 },
                { label: '>3倍', color: COLORS.red, count: integrityData?.red ?? 0 },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '2px 0',
                    fontSize: 13,
                  }}
                >
                  <span>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: item.color,
                        marginRight: 6,
                      }}
                    />
                    {item.label}
                  </span>
                  <Text strong>{item.count}台</Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );

  // ============================================================
  // Render: Mode Toggle + Time Selector
  // ============================================================
  const renderControls = () => (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row justify="space-between" align="middle" gutter={16}>
        <Col>
          <Space size="middle">
            <Segmented<ModeType>
              value={mode}
              onChange={(val) => handleModeChange(val)}
              options={[
                {
                  label: (
                    <Space size={4}>
                      <ThunderboltOutlined />
                      <span>能量模式</span>
                    </Space>
                  ),
                  value: 'energy',
                },
                {
                  label: (
                    <Space size={4}>
                      <DashboardOutlined />
                      <span>功率模式</span>
                    </Space>
                  ),
                  value: 'power',
                },
              ]}
            />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {mode === 'energy' ? '累计电量 (kWh)' : '实时功率 (kW)'}
            </Text>
          </Space>
        </Col>
        <Col>
          <Space>
            <DatePicker showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" value={startTime} onChange={(d) => d && setStartTime(d)} style={{ width: 180 }} placeholder="开始时间" />
            <span>-</span>
            <DatePicker showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" value={endTime} onChange={(d) => d && setEndTime(d)} style={{ width: 180 }} placeholder="结束时间" />
          </Space>
        </Col>
      </Row>
    </Card>
  );

  // ============================================================
  // Render: Six-Petal Flower (六瓣梅花)
  // ============================================================
  const renderFlower = () => {
    if (loading || petals.length === 0) {
      return (
        <div
          style={{
            width: '100%',
            height: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin size="large" tip="加载仪表盘数据..." />
        </div>
      );
    }

    const flowerSize = 560; // px - overall diameter
    const petalRadius = 170; // px - distance from center to petal center
    const petalSize = 180; // px - each petal chart diameter
    const centerSize = 130; // px - center SOC circle
    const centerX = flowerSize / 2;
    const centerY = flowerSize / 2;

    // Map clock position to angle in degrees (from top, clockwise)
    // 12=top(0°), 3=right(90°), 6=bottom(180°), 9=left(270°)
    const clockAngleMap: Record<number, number> = {
      12: 0,
      10: 300,  // 60° counter-clockwise from top
      2: 60,    // 60° clockwise from top
      8: 240,   // 120° counter-clockwise from top
      4: 120,   // 120° clockwise from top
      6: 180,
    };

    return (
      <Card
        title={
          <Space>
            <DashboardOutlined />
            <span>六瓣梅花图 — 能量流向概览</span>
          </Space>
        }
        size="small"
        extra={
          <Text style={{ fontSize: 12, color: COLORS.textSecondary }}>
            {mode === 'energy' ? '单位: kWh' : '单位: kW'}
          </Text>
        }
      >
        <div style={{ position: 'relative', width: '100%', paddingTop: 10 }}>
          {/* Legend: top vs bottom labels */}
          <Row justify="center" style={{ marginBottom: 8 }}>
            <Space size={32}>
              <Text style={{ color: COLORS.green, fontSize: 13 }}>
                ↑ 给系统供电 (光伏·电网·储能)
              </Text>
              <Text style={{ color: COLORS.red, fontSize: 13 }}>
                ↓ 从系统取电 (负载·倒送·充电)
              </Text>
            </Space>
          </Row>

          {/* Flower container */}
          <div
            style={{
              position: 'relative',
              width: flowerSize,
              height: flowerSize,
              margin: '0 auto',
            }}
          >
            {/* Background subtle circle */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: flowerSize,
                height: flowerSize,
                borderRadius: '50%',
                border: '1px dashed #e8e8e8',
                pointerEvents: 'none',
              }}
            />

            {/* Petal gauge charts */}
            {petals.map((petal) => {
              const angleDeg = clockAngleMap[petal.clockPos] ?? 0;
              const angleRad = (angleDeg * Math.PI) / 180;
              const cx = centerX + petalRadius * Math.sin(angleRad);
              const cy = centerY - petalRadius * Math.cos(angleRad);

              return (
                <div
                  key={petal.key}
                  style={{
                    position: 'absolute',
                    left: cx - petalSize / 2,
                    top: cy - petalSize / 2,
                    width: petalSize,
                    height: petalSize,
                  }}
                >
                  {/* Gauge chart */}
                  <div style={{ width: petalSize, height: petalSize }}>
                    <Doughnut
                      data={getGaugeData(petal)}
                      options={gaugeOptions}
                    />
                  </div>
                  {/* Center label inside gauge */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      pointerEvents: 'none',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: petal.color,
                        lineHeight: 1.2,
                      }}
                    >
                      {formatShort(petal.value)}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: COLORS.textSecondary,
                        lineHeight: 1.2,
                        marginTop: 2,
                      }}
                    >
                      {petal.unit}
                    </div>
                  </div>
                  {/* Petal label below gauge */}
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bottom: -4,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Tooltip title={petal.description}>
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: COLORS.textPrimary,
                        }}
                      >
                        {petal.label}
                      </Text>
                    </Tooltip>
                  </div>
                </div>
              );
            })}

            {/* Center: Battery SOC circle */}
            <div
              style={{
                position: 'absolute',
                left: centerX - centerSize / 2,
                top: centerY - centerSize / 2,
                width: centerSize,
                height: centerSize,
              }}
            >
              {socLoading ? (
                <Spin />
              ) : (
                <>
                  <Doughnut data={socChartData} options={socOptions} />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
                      电池SOC
                    </div>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color:
                          (batterySoc?.currentSoc ?? 50) >= 50
                            ? COLORS.green
                            : (batterySoc?.currentSoc ?? 50) >= 20
                            ? COLORS.gold
                            : COLORS.red,
                        lineHeight: 1.2,
                      }}
                    >
                      {(batterySoc?.currentSoc ?? 0).toFixed(0)}%
                    </div>
                    {batterySoc && (
                      <div
                        style={{
                          fontSize: 12,
                          color:
                            (batterySoc.delta ?? 0) >= 0
                              ? COLORS.green
                              : COLORS.red,
                          marginTop: 2,
                        }}
                      >
                        {batterySoc.delta >= 0 ? (
                          <ArrowUpOutlined />
                        ) : (
                          <ArrowDownOutlined />
                        )}
                        {Math.abs(batterySoc.delta ?? 0).toFixed(1)}%
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Energy flow summary labels below flower */}
          <Row justify="center" style={{ marginTop: 16 }} gutter={48}>
            <Col>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  系统供电合计
                </Text>
                <div style={{ fontSize: 20, fontWeight: 600, color: COLORS.green }}>
                  {formatShort(
                    (overviewData?.pv?.total ?? 0) +
                      (overviewData?.gridSupply ?? 0) +
                      (overviewData?.batteryDischarge ?? 0),
                  )}
                  <Text style={{ fontSize: 13, color: COLORS.textSecondary, marginLeft: 4 }}>
                    {mode === 'energy' ? 'kWh' : 'kW'}
                  </Text>
                </div>
              </div>
            </Col>
            <Col>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  系统取电合计
                </Text>
                <div style={{ fontSize: 20, fontWeight: 600, color: COLORS.red }}>
                  {formatShort(
                    (overviewData?.loadTotal ?? 0) +
                      (overviewData?.gridExport ?? 0) +
                      (overviewData?.batteryCharge ?? 0),
                  )}
                  <Text style={{ fontSize: 13, color: COLORS.textSecondary, marginLeft: 4 }}>
                    {mode === 'energy' ? 'kWh' : 'kW'}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Card>
    );
  };

  // ============================================================
  // Render: Summary Cards (quick stats below the flower)
  // ============================================================
  const renderSummaryCards = () => (
    <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
      {[
        {
          title: '光伏发电',
          value: overviewData?.pv?.total ?? 0,
          color: COLORS.gold,
          icon: '☀️',
          key: 'pvSummary',
        },
        {
          title: '电网供电',
          value: overviewData?.gridSupply ?? 0,
          color: COLORS.blue,
          icon: '⚡',
          key: 'gridSupplySummary',
        },
        {
          title: '储能放电',
          value: overviewData?.batteryDischarge ?? 0,
          color: COLORS.green,
          icon: '🔋',
          key: 'batteryDischargeSummary',
        },
        {
          title: '负载用电',
          value: overviewData?.loadTotal ?? 0,
          color: COLORS.red,
          icon: '🏭',
          key: 'loadSummary',
        },
        {
          title: '向电网倒送',
          value: overviewData?.gridExport ?? 0,
          color: COLORS.orange,
          icon: '↗️',
          key: 'gridExportSummary',
        },
        {
          title: '向储能充电',
          value: overviewData?.batteryCharge ?? 0,
          color: COLORS.purple,
          icon: '🔌',
          key: 'batteryChargeSummary',
        },
      ].map((item) => (
        <Col key={item.key} xs={12} sm={8} md={4}>
          <Card size="small" hoverable>
            <Statistic
              title={
                <Space size={4}>
                  <span>{item.icon}</span>
                  <span>{item.title}</span>
                </Space>
              }
              value={item.value}
              precision={1}
              suffix={mode === 'energy' ? 'kWh' : 'kW'}
              valueStyle={{ color: item.color, fontSize: 22, fontWeight: 600 }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );

  // ============================================================
  // Main Render
  // ============================================================
  return (
    <div style={{ padding: 16, background: COLORS.grey, minHeight: '100vh' }}>
      {/* Page Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <DashboardOutlined style={{ marginRight: 8 }} />
            纬诚电表监控系统 — 仪表盘
          </Title>
        </Col>
        <Col>
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              最后更新: {dayjs().format('YYYY-MM-DD HH:mm:ss')}
            </Text>
            <Tooltip title="刷新数据">
              <ReloadOutlined
                style={{ fontSize: 16, cursor: 'pointer', color: COLORS.blue }}
                onClick={fetchAllData}
              />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {/* Controls */}
      {renderControls()}

      {/* Top Row: Integrity Pie */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={8}>
          {renderIntegrityCard()}
        </Col>
        <Col xs={24} lg={16}>
          {/* Empty space or additional info could go here */}
          <Card size="small" style={{ height: '100%' }}>
            <Space direction="vertical" size="small">
              <Text strong>系统状态摘要</Text>
              {overviewData ? (
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="光伏+电网+储能供电"
                      value={
                        (overviewData.pv?.total ?? 0) +
                        (overviewData.gridSupply ?? 0) +
                        (overviewData.batteryDischarge ?? 0)
                      }
                      precision={1}
                      suffix={mode === 'energy' ? 'kWh' : 'kW'}
                      valueStyle={{ fontSize: 18, color: COLORS.green }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="负载+倒送+充电取电"
                      value={
                        (overviewData.loadTotal ?? 0) +
                        (overviewData.gridExport ?? 0) +
                        (overviewData.batteryCharge ?? 0)
                      }
                      precision={1}
                      suffix={mode === 'energy' ? 'kWh' : 'kW'}
                      valueStyle={{ fontSize: 18, color: COLORS.red }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="电池SOC"
                      value={batterySoc?.currentSoc ?? 0}
                      precision={1}
                      suffix="%"
                      valueStyle={{
                        fontSize: 18,
                        color:
                          (batterySoc?.currentSoc ?? 50) >= 50
                            ? COLORS.green
                            : (batterySoc?.currentSoc ?? 50) >= 20
                            ? COLORS.gold
                            : COLORS.red,
                      }}
                    />
                  </Col>
                </Row>
              ) : (
                <Skeleton active paragraph={{ rows: 1 }} />
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Six-Petal Flower */}
      {renderFlower()}

      {/* Summary Cards */}
      {renderSummaryCards()}
    </div>
  );
};

export default Dashboard;
